import * as React from 'react'
import { useMemo, useState } from 'react'

import { DateHelpers } from '@crm/shared/lib'

import { getNextDateInputValue, normalizeDateInputOnBlur } from './formatDateInput'

type UseDatePickerParams = {
  value: string
  disabled?: boolean
  startMonth: Date
  endMonth: Date
  parseDate?: (value: string) => Date | undefined
  formatDate?: (date: Date | undefined) => string
  normalizeOnBlur?: boolean
  onChange?: (value: string) => void
  onKeyDown?: React.KeyboardEventHandler<HTMLInputElement>
}

export function useDatePicker({
  value,
  disabled,
  startMonth,
  endMonth,
  parseDate = DateHelpers.parseDisplayDate,
  formatDate = DateHelpers.formatDisplayDate,
  normalizeOnBlur = true,
  onChange,
  onKeyDown,
}: UseDatePickerParams) {
  const [open, setOpen] = useState(false)
  const selectedDate = useMemo(() => parseDate(value), [parseDate, value])
  const [month, setMonth] = useState<Date | undefined>(selectedDate ?? new Date())

  const handleInputChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const nextValue = getNextDateInputValue(
      event.target.value,
      {
        minYear: startMonth.getFullYear(),
        maxYear: endMonth.getFullYear(),
      },
      {
        inputType: (event.nativeEvent as InputEvent).inputType ?? '',
        previousValue: value,
        selectionStart: event.target.selectionStart,
      },
    )
    const nextDate = parseDate(nextValue)

    onChange?.(nextValue)

    if (nextDate) {
      setMonth(nextDate)
    }
  }

  const handleInputKeyDown = (event: React.KeyboardEvent<HTMLInputElement>) => {
    onKeyDown?.(event)

    if (event.defaultPrevented) {
      return
    }

    if (event.key === 'ArrowDown') {
      event.preventDefault()
      setOpen(true)
    }
  }

  const handleNormalizeInputOnBlur = (event: React.FocusEvent<HTMLInputElement>) => {
    if (normalizeOnBlur) {
      const normalizedValue = normalizeDateInputOnBlur(event.currentTarget.value, parseDate)

      if (normalizedValue !== event.currentTarget.value) {
        event.currentTarget.value = normalizedValue
        onChange?.(normalizedValue)
        setMonth(parseDate(normalizedValue))
      }
    }
  }

  const handleSelectDate = (date: Date | undefined) => {
    if (disabled) {
      return
    }

    onChange?.(formatDate(date))

    if (date) {
      setMonth(date)
    }

    setOpen(false)
  }

  return {
    month,
    open,
    selectedDate,
    setMonth,
    setOpen,
    handleNormalizeInputOnBlur,
    handleInputChange,
    handleInputKeyDown,
    handleSelectDate,
  }
}
